import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
    withStyles,
    Button,
    CircularProgress,
    Typography,
    FormGroup,
    Paper,
    Switch,
    InputLabel,
    FormControl,
    FormControlLabel,
    TextField,
    Grid,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { BackButton, hasPermission, safeParse } from '../../../../sc-utilities';
import * as deviceCasePreselectionActions from '../../store/deviceCasePreselectionActions';
import History from '../../../../../History';

const styles = (theme) => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    form: {
        width: '95%',
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: '100%',
        marginTop: '10px',
    },
    formControl: {
        width: '100%',
    },
});

const FormFields = {
    smart_score_threshold: 'smart_score_threshold',
    require_is_healthy: 'require_is_healthy',
};

const DeviceCasePreselectionDiagnoseDetailsDriveHealth = (props) => {
    const { classes, translations, permissions, activeCustomerID, onUpdateDeviceCasePreselectionDiagnoseCaseParams } =
        props;

    const deviceCasePreselectionDiagnoseCase =
        props.location.state?.redirectOptions?.rowData?.DeviceCasePreselectionDiagnoseCase ?? null;
    const params = safeParse(deviceCasePreselectionDiagnoseCase?.Params);

    const initialValues = {
        smart_score_threshold: params?.smart_score_threshold ?? 70,
        require_is_healthy: params?.require_is_healthy ?? true,
    };

    const hasDiagnoseCaseUpdatePermission = hasPermission(permissions, 'device_case_preselection_update');

    const formSettings = {
        Validation: {
            smart_score_threshold: Yup.number().min(0).max(100),
            require_is_healthy: Yup.boolean(),
        },
        FormTitle: translations['update_parameters'] || 'Update parameters',
        ButtonSubmit: hasDiagnoseCaseUpdatePermission
            ? {
                  Title: translations['save'] || 'SAVE',
              }
            : null,
    };

    const handleEdit = async (data) => {
        await onUpdateDeviceCasePreselectionDiagnoseCaseParams({
            Params: {
                smart_score_threshold: Number(data.smart_score_threshold),
                require_is_healthy: !!data.require_is_healthy,
            },
            DiagnoseCaseID: deviceCasePreselectionDiagnoseCase?.DiagnoseCaseID,
            DeviceCasePreselectionID: deviceCasePreselectionDiagnoseCase?.DeviceCasePreselectionID,
            ActiveCustomerID: activeCustomerID,
        });
        History.goBack();
    };
    return (
        <Paper className={classes.root}>
            {BackButton(translations, classes.customTooltip)}
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={handleEdit}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {(formikProps) => {
                    const { values, isSubmitting, isValid, dirty, handleSubmit, handleChange, setFieldValue } =
                        formikProps;
                    return (
                        <form className={classes.form} onSubmit={handleSubmit}>
                            <>
                                <Typography align="center" variant={'h5'}>
                                    {translations['drive_health'] || 'Drive Health'}
                                </Typography>

                                <Grid item xs={12} sm={6}>
                                    <FormGroup>
                                        <FormControl className={classes.formControl} margin={'dense'}>
                                            <TextField
                                                name={FormFields.smart_score_threshold}
                                                value={values.smart_score_threshold}
                                                label={`${
                                                    translations['smart_score_threshold'] || 'SMART score threshold'
                                                }`}
                                                margin={'dense'}
                                                fullWidth
                                                type={'number'}
                                                onChange={handleChange}
                                                InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }}
                                                helperText={
                                                    translations['smart_score_threshold_hint'] ||
                                                    'Per-drive SMART score (0-100) below which the case fails.'
                                                }
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormGroup>
                                        <FormControl className={classes.formControl} margin={'dense'}>
                                            <InputLabel shrink>
                                                {translations['require_is_healthy'] || 'Require drive IsHealthy = true'}
                                            </InputLabel>
                                            <FormControlLabel
                                                style={{ paddingTop: 24 }}
                                                control={
                                                    <Switch
                                                        checked={values.require_is_healthy}
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                FormFields.require_is_healthy,
                                                                e.target.checked,
                                                            )
                                                        }
                                                        name={FormFields.require_is_healthy}
                                                        color="primary"
                                                    />
                                                }
                                                label={
                                                    values.require_is_healthy
                                                        ? translations['enabled'] || 'Enabled'
                                                        : translations['disabled'] || 'Disabled'
                                                }
                                            />
                                        </FormControl>
                                    </FormGroup>
                                </Grid>

                                <div>
                                    {formSettings.ButtonSubmit ? (
                                        <Button
                                            fullWidth={true}
                                            className={classes.submit}
                                            variant="contained"
                                            color={formSettings.ButtonSubmit.Color}
                                            type="submit"
                                            style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                        >
                                            {isSubmitting ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                formSettings.ButtonSubmit.Title
                                            )}
                                        </Button>
                                    ) : null}
                                </div>
                            </>
                        </form>
                    );
                }}
            </Formik>
        </Paper>
    );
};
const mapStateToProps = (state) => {
    return {
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        onUpdateDeviceCasePreselectionDiagnoseCaseParams: (params) =>
            dispatch(deviceCasePreselectionActions.editDeviceCasePreselectionDiagnoseCaseParams(params)),
    };
};
export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles)(DeviceCasePreselectionDiagnoseDetailsDriveHealth));
